import React, { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Fade } from '@mui/material';

interface RouteTransitionWrapperProps {
  children: ReactNode;
}

/**
 * RouteTransitionWrapper manages route transitions and forces proper cleanup
 * between route changes, especially when navigating between different route hierarchies.
 * This solves the issue where React Router doesn't properly unmount components
 * when transitioning from deeply nested routes to shallow routes.
 */
export const RouteTransitionWrapper: React.FC<RouteTransitionWrapperProps> = ({ children }) => {
  const location = useLocation();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayChildren, setDisplayChildren] = useState(children);
  const [prevPath, setPrevPath] = useState(location.pathname);

  useEffect(() => {
    // Check if we're navigating within the docs section
    const isDocsRoute = (path: string) => path.startsWith('/docs');
    const stayingInDocs = isDocsRoute(prevPath) && isDocsRoute(location.pathname);

    // Skip transition if staying within docs to prevent flashing
    if (stayingInDocs) {
      setDisplayChildren(children);
      setPrevPath(location.pathname);
      return;
    }

    // Start transition for other route changes
    setIsTransitioning(true);

    // Small delay to ensure state cleanup
    const transitionTimer = setTimeout(() => {
      setDisplayChildren(children);
      setIsTransitioning(false);
      setPrevPath(location.pathname);
    }, 50);

    return () => {
      clearTimeout(transitionTimer);
    };
  }, [location.pathname, children, prevPath]);

  // Force remount by using a key that changes with transitions
  // But keep the same key when navigating within docs to prevent flashing
  const isDocsRoute = (path: string) => path.startsWith('/docs');
  const inDocs = isDocsRoute(location.pathname);

  const transitionKey = inDocs
    ? 'docs-stable' // Use same key for all docs pages
    : isTransitioning
      ? `transitioning-${location.pathname}`
      : `stable-${location.pathname}`;

  return (
    <Box
      key={transitionKey}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Skip fade animation when in docs to prevent flashing */}
      {inDocs ? (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
          {displayChildren}
        </Box>
      ) : (
        <Fade in={!isTransitioning} timeout={200}>
          <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {displayChildren}
          </Box>
        </Fade>
      )}
    </Box>
  );
};